import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { TenantSortField, TenantStatus } from '@/types/Tenant';

export type SlugValidationStatus =
  | 'empty'
  | 'invalid-chars'
  | 'reserved'
  | 'valid';

export type TenantStatusFilter = 'all' | TenantStatus;

export type OnboardDraft = {
  displayName: string;
  slug: string;
  primaryAdminEmail: string;
  defaultLanguageCode: string;
  plan: string;
  forceAllowSlug: boolean;
};

export type PlatformTenantsPageState = {
  filters: {
    search: string;
    status: TenantStatusFilter;
    plan: string;
  };
  // The directory sort field; sort direction is fixed per field by the web-api
  // (displayName / status ascending, createdAt descending — UC-85 §5.2.a).
  sort: {
    field: TenantSortField;
  };
  onboardModalOpen: boolean;
  onboardDraft: OnboardDraft;
  slugValidationStatus: SlugValidationStatus;
  submitInProgress: boolean;
  lastErrorKey: string | null;
};

const emptyDraft: OnboardDraft = {
  displayName: '',
  slug: '',
  primaryAdminEmail: '',
  defaultLanguageCode: '',
  plan: '',
  forceAllowSlug: false,
};

const initialState: PlatformTenantsPageState = {
  filters: {
    search: '',
    status: 'all',
    plan: '',
  },
  sort: {
    field: 'displayName',
  },
  onboardModalOpen: false,
  onboardDraft: emptyDraft,
  slugValidationStatus: 'empty',
  submitInProgress: false,
  lastErrorKey: null,
};

export const platformTenantsPageSlice = createSlice({
  name: 'platformTenantsPage',
  initialState,
  reducers: {
    setSearch(state, action: PayloadAction<string>) {
      state.filters.search = action.payload;
    },
    setStatusFilter(state, action: PayloadAction<TenantStatusFilter>) {
      state.filters.status = action.payload;
    },
    setPlanFilter(state, action: PayloadAction<string>) {
      state.filters.plan = action.payload;
    },
    setSortField(state, action: PayloadAction<TenantSortField>) {
      state.sort.field = action.payload;
    },
    openOnboardModal(state) {
      state.onboardModalOpen = true;
      state.onboardDraft = emptyDraft;
      state.slugValidationStatus = 'empty';
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeOnboardModal(state) {
      state.onboardModalOpen = false;
      state.onboardDraft = emptyDraft;
      state.slugValidationStatus = 'empty';
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    setSlugValidationStatus(state, action: PayloadAction<SlugValidationStatus>) {
      state.slugValidationStatus = action.payload;
    },
    setSubmitInProgress(state, action: PayloadAction<boolean>) {
      state.submitInProgress = action.payload;
    },
    setLastErrorKey(state, action: PayloadAction<string | null>) {
      state.lastErrorKey = action.payload;
    },
  },
});

export const {
  setSearch,
  setStatusFilter,
  setPlanFilter,
  setSortField,
  openOnboardModal,
  closeOnboardModal,
  setSlugValidationStatus,
  setSubmitInProgress,
  setLastErrorKey,
} = platformTenantsPageSlice.actions;

type AppRootState = {
  platformTenantsPage: PlatformTenantsPageState;
};

export const selectPlatformTenantsPage = (state: AppRootState) =>
  state.platformTenantsPage;
